import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "../../components/Button/Button";
import C3Chart from "./C3Chart";
import { safeUrl } from "../../utils/url";
import { StatusBadge } from "./Accounts";
import Publications, { Thumbnail, latestViews } from "./Publications";
import {
    fetchGrowth,
    fetchLag,
    fetchLiveGames,
    fetchPostImpact,
    fetchProjects,
    fetchSummary,
} from "../../redux/marketingSlice";
import {
    ACCOUNT_STATUS,
    CONNECTABLE_PLATFORMS,
    PLATFORMS,
    SERIES_COLORS,
    average,
    defaultRange,
    formatCompact,
    formatDateTime,
    formatNumber,
    formatPercent,
    sum,
    toDayKey,
} from "./marketingUtils";

const TABS = [
    { key: "users", label: "Usuarios" },
    { key: "retention", label: "Retención" },
    { key: "social", label: "Redes sociales" },
    { key: "impact", label: "Marketing × Roblox" },
];

const GAME_METRICS = {
    newFromSearch: "Nuevos desde búsqueda",
    newUsers: "Usuarios nuevos",
    dau: "Usuarios activos (DAU)",
    visits: "Visitas",
};

const PROJECT_KEY = "ashara_marketing_project";

const readStoredProject = () => {
    try {
        return localStorage.getItem(PROJECT_KEY);
    } catch {
        return null;
    }
};

const storeProject = (id) => {
    try {
        localStorage.setItem(PROJECT_KEY, id);
    } catch {
        // Sin almacenamiento local: solo se pierde la preferencia.
    }
};

const timeAxis = {
    x: { type: "timeseries", tick: { format: "%d %b", culling: { max: 8 } } },
    y: { tick: { format: formatCompact }, min: 0, padding: { bottom: 0 } },
};

const hashtagScore = (avgViews, overallAvg) =>
    avgViews === null || avgViews === undefined || !overallAvg ? null : Math.round((avgViews / overallAvg) * 10) / 10;

// Series de la gráfica de Marketing × Roblox (orden fijo de colores).
const IMPACT_SERIES = [
    { key: "views", label: "Vistas", type: "bar", color: SERIES_COLORS[0], value: (day) => day.marketing?.total ?? 0 },
    { key: "dau", label: "DAU", type: "line", color: SERIES_COLORS[1], value: (day) => day.game?.dau ?? null },
    { key: "newUsers", label: "Nuevos", type: "line", color: SERIES_COLORS[2], value: (day) => day.game?.newUsers ?? null },
    { key: "returningUsers", label: "Recurrentes", type: "line", color: "#c98500", value: (day) => day.game?.returningUsers ?? null },
];

const returningShare = (day) => (day.game?.dau ? (day.game.returningUsers / day.game.dau) * 100 : null);

// ── Piezas comunes ───────────────────────────────────────────────────────────

function StatTile({ label, value, hint }) {
    return (
        <div className="mk-stat">
            <span className="mk-stat-value">{value}</span>
            <span className="mk-stat-label">{label}</span>
            {hint && <span className="mk-stat-hint">{hint}</span>}
        </div>
    );
}

function ChartPanel({ title, subtitle, children }) {
    return (
        <section className="mk-panel">
            <h2 className="mk-panel-title">{title}</h2>
            {subtitle && <p className="mk-panel-subtitle">{subtitle}</p>}
            {children}
        </section>
    );
}

function RequestState({ request, empty, children }) {
    if (request.status === "idle" || (request.status === "loading" && !request.data)) {
        return <div className="mk-center"><div className="mk-spinner" /><span>Cargando…</span></div>;
    }
    if (request.status === "failed") return <div className="mk-center mk-error">{request.error}</div>;
    if (empty) return <div className="mk-center">{empty}</div>;
    return children;
}

function NoGames({ projectName }) {
    return (
        <div className="mk-center">
            <span>{projectName} no tiene juegos de Roblox vinculados.</span>
            <Link to="/Marketing/Accounts" className="mk-link">Vincular un juego en Proyectos y conexiones</Link>
        </div>
    );
}

// ── Pestañas ─────────────────────────────────────────────────────────────────

function UsersTab({ growth, live, project }) {
    const days = useMemo(() => growth.data?.days || [], [growth.data]);
    const withGame = days.filter((day) => day.game);
    const hasGames = (growth.data?.games || []).length > 0;

    const chart = useMemo(() => {
        if (!withGame.length) return null;
        return {
            data: {
                x: "x",
                columns: [
                    ["x", ...days.map((day) => day.date)],
                    ["DAU", ...days.map((day) => day.game?.dau ?? null)],
                    ["Nuevos", ...days.map((day) => day.game?.newUsers ?? null)],
                    ["Recurrentes", ...days.map((day) => day.game?.returningUsers ?? null)],
                ],
                colors: { DAU: SERIES_COLORS[0], Nuevos: SERIES_COLORS[1], Recurrentes: SERIES_COLORS[2] },
            },
            axis: timeAxis,
            tooltip: { format: { value: formatNumber } },
        };
    }, [days, withGame.length]);

    const sources = useMemo(() => {
        const totals = {};
        withGame.forEach((day) => {
            Object.entries(day.game.newBySource || {}).forEach(([source, value]) => {
                totals[source] = (totals[source] || 0) + value;
            });
        });
        const total = sum(Object.values(totals));
        return Object.entries(totals)
            .sort((a, b) => b[1] - a[1])
            .map(([source, value]) => ({ source, value, share: total ? (value / total) * 100 : 0 }));
    }, [withGame]);

    return (
        <RequestState
            request={growth}
            empty={!hasGames ? <NoGames projectName={project.name} /> : !withGame.length ? "Roblox aún no tiene datos para este rango (se publican con ~1 día de retraso)." : null}
        >
            <div className="mk-stats">
                <StatTile label="DAU promedio" value={formatNumber(average(withGame.map((day) => day.game.dau)))} />
                <StatTile label="Usuarios nuevos" value={formatCompact(sum(withGame.map((day) => day.game.newUsers)))} hint="Total del periodo" />
                <StatTile label="Recurrentes promedio" value={formatNumber(average(withGame.map((day) => day.game.returningUsers)))} hint="Por día" />
                <StatTile label="Visitas" value={formatCompact(sum(withGame.map((day) => day.game.visits)))} hint="Total del periodo" />
                <StatTile label="Pico de jugadores" value={formatNumber(Math.max(...withGame.map((day) => day.game.peakConcurrent ?? 0)) || null)} hint="Concurrentes, máximo diario" />
            </div>

            {live.data?.length > 0 && (
                <section className="mk-panel">
                    <h2 className="mk-panel-title">En vivo</h2>
                    <div className="mk-stats">
                        {live.data.map((game) => (
                            <StatTile
                                key={game.gameId}
                                label={game.name}
                                value={formatNumber(game.playing)}
                                hint={`Jugando ahora · ${formatCompact(game.visits)} visitas · ${formatCompact(game.favorites)} favoritos`}
                            />
                        ))}
                    </div>
                </section>
            )}

            <ChartPanel title="Usuarios por día" subtitle="DAU, usuarios nuevos y recurrentes de los juegos vinculados (sumados si hay varios).">
                <C3Chart config={chart} label="Usuarios por día" />
            </ChartPanel>

            {sources.length > 0 && (
                <ChartPanel title="Usuarios nuevos por fuente">
                    <table className="mk-table">
                        <thead>
                            <tr><th>Fuente</th><th className="mk-num">Nuevos</th><th>Participación</th></tr>
                        </thead>
                        <tbody>
                            {sources.map((row) => (
                                <tr key={row.source}>
                                    <td>{row.source}</td>
                                    <td className="mk-num">{formatNumber(row.value)}</td>
                                    <td>
                                        <span className="mk-bar"><span className="mk-bar-fill" style={{ width: `${row.share}%` }} /></span>
                                        <span className="mk-bar-label">{formatPercent(row.share)}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </ChartPanel>
            )}
        </RequestState>
    );
}

function RetentionTab({ growth, summary, project }) {
    const days = useMemo(() => growth.data?.days || [], [growth.data]);
    const withGame = days.filter((day) => day.game?.dau);
    const hasGames = (growth.data?.games || []).length > 0;

    const chart = useMemo(() => {
        if (!withGame.length) return null;
        return {
            data: {
                x: "x",
                columns: [
                    ["x", ...days.map((day) => day.date)],
                    ["% recurrentes", ...days.map((day) => {
                        const value = returningShare(day);
                        return value === null ? null : Math.round(value * 10) / 10;
                    })],
                ],
                colors: { "% recurrentes": SERIES_COLORS[0] },
            },
            legend: { show: false },
            axis: { ...timeAxis, y: { min: 0, max: 100, padding: { top: 0, bottom: 0 }, tick: { format: (value) => `${value}%` } } },
            tooltip: { format: { value: formatPercent } },
        };
    }, [days, withGame.length]);

    const platforms = summary.data?.platforms || [];
    const maxRetention = Math.max(100, ...platforms.map((row) => row.avgRetention || 0));

    return (
        <>
            <RequestState
                request={growth}
                empty={!hasGames ? <NoGames projectName={project.name} /> : !withGame.length ? "Roblox aún no tiene datos para este rango." : null}
            >
                <div className="mk-stats">
                    <StatTile label="Recurrentes / DAU" value={formatPercent(average(withGame.map(returningShare)))} hint="Promedio diario" />
                    <StatTile
                        label="Nuevos / DAU"
                        value={formatPercent(average(withGame.map((day) => (day.game.newUsers / day.game.dau) * 100)))}
                        hint="Promedio diario"
                    />
                    <StatTile label="Recurrentes promedio" value={formatNumber(average(withGame.map((day) => day.game.returningUsers)))} hint="Jugadores por día" />
                </div>
                <ChartPanel
                    title="Jugadores que regresan"
                    subtitle="Porcentaje del DAU que ya había jugado antes. Roblox no expone retención por cohortes (D1/D7) en esta API."
                >
                    <C3Chart config={chart} label="Porcentaje de usuarios recurrentes por día" />
                </ChartPanel>
            </RequestState>

            <ChartPanel title="Retención de videos" subtitle="Retención promedio por red social en el periodo. Instagram y TikTok no entregan este dato.">
                <RequestState request={summary} empty={platforms.length === 0 ? "No hay publicaciones en este rango." : null}>
                    <table className="mk-table">
                        <thead>
                            <tr><th>Red</th><th className="mk-num">Publicaciones</th><th>Retención promedio</th></tr>
                        </thead>
                        <tbody>
                            {platforms.map((row) => (
                                <tr key={row.platform}>
                                    <td><PlatformName platform={row.platform} /></td>
                                    <td className="mk-num">{formatNumber(row.publications)}</td>
                                    <td>
                                        {row.avgRetention === null ? (
                                            <span className="mk-muted">No disponible</span>
                                        ) : (
                                            <>
                                                <span className="mk-bar">
                                                    <span
                                                        className="mk-bar-fill"
                                                        style={{ width: `${(row.avgRetention / maxRetention) * 100}%`, background: PLATFORMS[row.platform]?.color }}
                                                    />
                                                </span>
                                                <span className="mk-bar-label">{formatPercent(row.avgRetention)}</span>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </RequestState>
            </ChartPanel>
        </>
    );
}

function PlatformName({ platform }) {
    const info = PLATFORMS[platform];
    return (
        <span className="mk-platform">
            <span className="mk-swatch" style={{ background: info?.color }} />
            {info && <FontAwesomeIcon icon={info.icon} />} {info?.label || platform}
        </span>
    );
}

function SocialTab({ growth, summary, project, range }) {
    const publicationRows = useSelector((state) => state.marketing.publications.data?.rows);
    const days = useMemo(() => growth.data?.days || [], [growth.data]);
    const accounts = project.accounts || [];
    const activePlatforms = useMemo(
        () => CONNECTABLE_PLATFORMS.filter((key) => days.some((day) => day.marketing?.byPlatform?.[key])),
        [days]
    );

    const chart = useMemo(() => {
        if (!activePlatforms.length) return null;
        const names = activePlatforms.map((key) => PLATFORMS[key].label);
        return {
            data: {
                x: "x",
                columns: [
                    ["x", ...days.map((day) => day.date)],
                    ...activePlatforms.map((key) => [PLATFORMS[key].label, ...days.map((day) => day.marketing?.byPlatform?.[key] ?? 0)]),
                ],
                type: "bar",
                groups: [names],
                order: null,
                colors: Object.fromEntries(activePlatforms.map((key) => [PLATFORMS[key].label, PLATFORMS[key].color])),
            },
            bar: { width: { ratio: 0.7 } },
            axis: timeAxis,
            tooltip: { format: { value: formatNumber } },
        };
    }, [days, activePlatforms]);

    // Mismas publicaciones que la tabla de videos, ordenadas por vistas actuales.
    const topPublications = useMemo(() => {
        const from = new Date(`${range.from}T00:00:00Z`).getTime();
        const to = new Date(`${range.to}T23:59:59.999Z`).getTime();
        return (publicationRows || [])
            .filter((row) => {
                const date = row.publishDate ? new Date(row.publishDate).getTime() : null;
                return date !== null && date >= from && date <= to && latestViews(row) !== null;
            })
            .sort((a, b) => latestViews(b) - latestViews(a))
            .slice(0, 3);
    }, [publicationRows, range]);
    const platforms = summary.data?.platforms || [];
    const hashtags = summary.data?.topHashtags || [];
    // Promedio de vistas actuales de todos los videos del periodo (todas las redes).
    const overallAvgViews = (() => {
        let views = 0;
        let videos = 0;
        platforms.forEach((row) => {
            if (row.totalViewsCurrent && row.avgViewsCurrent) {
                views += row.totalViewsCurrent;
                videos += row.totalViewsCurrent / row.avgViewsCurrent;
            }
        });
        return videos ? views / videos : null;
    })();
    const needsAttention = accounts.filter((account) => account.status !== "active");

    return (
        <>
            <div className="mk-stats">
                <StatTile label="Publicaciones" value={formatNumber(sum(platforms.map((row) => row.publications)))} hint="En el periodo" />
                <StatTile label="Vistas" value={formatCompact(sum(days.map((day) => day.marketing?.total)))} hint="Cuentas conectadas, por día" />
                <StatTile label="Cuentas conectadas" value={formatNumber(accounts.filter((account) => account.status === "active").length)} />
                <StatTile label="Requieren atención" value={formatNumber(needsAttention.length)} hint={needsAttention.length ? "Reconéctalas en Proyectos" : "Todo en orden"} />
            </div>

            <ChartPanel title="Cuentas del proyecto">
                {accounts.length === 0 ? (
                    <div className="mk-center">
                        <span>No hay cuentas conectadas.</span>
                        <Link to="/Marketing/Accounts" className="mk-link">Conectar redes sociales</Link>
                    </div>
                ) : (
                    <table className="mk-table">
                        <thead>
                            <tr><th>Cuenta</th><th>Red</th><th>Estado</th><th>Última sincronización</th></tr>
                        </thead>
                        <tbody>
                            {accounts.map((account) => {
                                const status = ACCOUNT_STATUS[account.status] || ACCOUNT_STATUS.disconnected;
                                return (
                                    <tr key={account._id}>
                                        <td>@{account.username}</td>
                                        <td><PlatformName platform={account.platform} /></td>
                                        <td><StatusBadge tone={status.tone} label={status.label} /></td>
                                        <td>{formatDateTime(account.lastSyncedAt)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </ChartPanel>

            <ChartPanel title="Vistas por día" subtitle="Vistas diarias de las cuentas conectadas, por red social.">
                <RequestState request={growth} empty={!chart ? "Aún no hay vistas registradas en este rango." : null}>
                    <C3Chart config={chart} label="Vistas por día por red social" />
                </RequestState>
            </ChartPanel>

            <ChartPanel title="Rendimiento por red" subtitle="Promedios por publicación en el periodo (24 h y 7 días después de publicar).">
                <RequestState request={summary} empty={platforms.length === 0 ? "No hay publicaciones en este rango." : null}>
                    <div className="mk-table-wrap">
                        <table className="mk-table">
                            <thead>
                                <tr>
                                    <th>Red</th>
                                    <th className="mk-num">Publicaciones</th>
                                    <th className="mk-num" title="Suma de las vistas actuales de las publicaciones del periodo">Vistas actuales</th>
                                    <th className="mk-num">Vistas 24 h</th>
                                    <th className="mk-num">Vistas 7 d</th>
                                    <th className="mk-num">Likes 7 d</th>
                                    <th className="mk-num">Comentarios 7 d</th>
                                    <th className="mk-num">Compartidos 7 d</th>
                                    <th className="mk-num" title="Likes ÷ vistas">Like rate 7 d</th>
                                    <th className="mk-num" title="% que se quedó a ver en vez de deslizar">Stayed to watch</th>
                                    <th className="mk-num">Retención</th>
                                </tr>
                            </thead>
                            <tbody>
                                {platforms.map((row) => (
                                    <tr key={row.platform}>
                                        <td><PlatformName platform={row.platform} /></td>
                                        <td className="mk-num">{formatNumber(row.publications)}</td>
                                        <td className="mk-num">{formatNumber(row.totalViewsCurrent ?? null)}</td>
                                        <td className="mk-num">{formatNumber(row.avgViews24h)}</td>
                                        <td className="mk-num">{formatNumber(row.avgViews7d)}</td>
                                        <td className="mk-num">{formatNumber(row.avgLikes7d)}</td>
                                        <td className="mk-num">{formatNumber(row.avgComments7d)}</td>
                                        <td className="mk-num">{formatNumber(row.avgShares7d)}</td>
                                        <td className="mk-num">{formatPercent(row.likeRate7d)}</td>
                                        <td className="mk-num" title={row.avgStayedToWatch === null && row.avgEngagedViewRate !== null ? "Estimado con vistas comprometidas ÷ vistas" : undefined}>
                                            {row.avgStayedToWatch !== null && row.avgStayedToWatch !== undefined
                                                ? formatPercent(row.avgStayedToWatch)
                                                : row.avgEngagedViewRate !== null && row.avgEngagedViewRate !== undefined
                                                    ? `≈ ${formatPercent(row.avgEngagedViewRate)}`
                                                    : "—"}
                                        </td>
                                        <td className="mk-num">{formatPercent(row.avgRetention)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </RequestState>
            </ChartPanel>

            <Publications project={project} range={range} />

            <div className="mk-columns">
                <ChartPanel title="Top 3 publicaciones">
                    {topPublications.length === 0 ? (
                        <p className="mk-muted">Sin publicaciones en este rango.</p>
                    ) : (
                        <table className="mk-table">
                            <thead><tr><th>Publicación</th><th className="mk-num">Vistas actuales</th></tr></thead>
                            <tbody>
                                {topPublications.map((item) => (
                                    <tr key={item.key}>
                                        <td>
                                            <span className="mk-pub">
                                                <Thumbnail row={item} />
                                                <span className="mk-pub-text">
                                                    <a href={safeUrl(item.url)} target="_blank" rel="noreferrer" className="mk-link">{item.postTitle}</a>
                                                    <span className="mk-muted">
                                                        <PlatformName platform={item.platform} /> · {new Date(item.publishDate).toLocaleDateString("es-CO")}
                                                    </span>
                                                </span>
                                            </span>
                                        </td>
                                        <td className="mk-num">{formatNumber(latestViews(item))}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </ChartPanel>

                <ChartPanel
                    title="Hashtags principales"
                    subtitle="Ordenados por las vistas actuales promedio de sus videos. La calificación compara ese promedio contra el promedio de todos tus videos del periodo."
                >
                    {hashtags.length === 0 ? (
                        <p className="mk-muted">Sin hashtags en este rango.</p>
                    ) : (
                        <table className="mk-table">
                            <thead>
                                <tr>
                                    <th>Hashtag</th>
                                    <th className="mk-num">Videos</th>
                                    <th className="mk-num">Vistas promedio</th>
                                    <th className="mk-num">Vistas totales</th>
                                    <th className="mk-num" title="Vistas promedio del hashtag ÷ vistas promedio de todos tus videos. 2x = el doble de lo normal">Calificación</th>
                                </tr>
                            </thead>
                            <tbody>
                                {hashtags.slice(0, 10).map((row) => {
                                    const score = hashtagScore(row.avgViewsCurrent, overallAvgViews);
                                    const scoreClass = score >= 2 ? " mk-outlier--high" : score !== null && score < 0.5 ? " mk-outlier--low" : "";
                                    return (
                                        <tr key={row.hashtag}>
                                            <td>#{row.hashtag}</td>
                                            <td className="mk-num">{formatNumber(row.publications)}</td>
                                            <td className="mk-num">{formatNumber(row.avgViewsCurrent ?? null)}</td>
                                            <td className="mk-num">{formatNumber(row.totalViewsCurrent ?? null)}</td>
                                            <td className={`mk-num${scoreClass}`}>{score === null ? "—" : `${formatNumber(score)}x`}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </ChartPanel>
            </div>
        </>
    );
}

function ImpactTab({ growth, impact, lag, metric, onMetricChange, project }) {
    const [visibleSeries, setVisibleSeries] = useState(IMPACT_SERIES.map((item) => item.key));
    const days = useMemo(() => growth.data?.days || [], [growth.data]);
    const hasGames = (growth.data?.games || []).length > 0;
    const metricLabel = GAME_METRICS[metric];

    // Una sola gráfica: vistas y jugadores tienen escalas muy distintas, así que
    // cada serie se expresa como índice (promedio del periodo = 100) en un mismo
    // eje. El tooltip muestra los valores reales.
    const combinedChart = useMemo(() => {
        const series = IMPACT_SERIES.filter((item) => visibleSeries.includes(item.key));
        if (!series.length) return null;
        const toIndex = (values) => {
            const base = average(values.filter((value) => value !== null && value > 0));
            return values.map((value) => (value === null || !base ? null : Math.round((value / base) * 1000) / 10));
        };
        const raw = Object.fromEntries(series.map((item) => [item.label, days.map(item.value)]));
        return {
            data: {
                x: "x",
                columns: [["x", ...days.map((day) => day.date)], ...series.map((item) => [item.label, ...toIndex(raw[item.label])])],
                types: Object.fromEntries(series.map((item) => [item.label, item.type])),
                colors: Object.fromEntries(series.map((item) => [item.label, item.color])),
            },
            legend: { show: false },
            bar: { width: { ratio: 0.7 } },
            axis: { ...timeAxis, y: { min: 0, padding: { bottom: 0 }, tick: { format: (value) => `${value}` } } },
            grid: { y: { show: true, lines: [{ value: 100, text: "Promedio del periodo" }] } },
            tooltip: {
                format: {
                    value: (value, ratio, id, index) =>
                        value === null ? "—" : `${formatNumber(raw[id]?.[index])} (índice ${formatNumber(value)})`,
                },
            },
        };
    }, [days, visibleSeries]);

    const toggleSeries = (key) =>
        setVisibleSeries((current) => (current.includes(key) ? current.filter((item) => item !== key) : [...current, key]));

    if (growth.status === "succeeded" && !hasGames) return <NoGames projectName={project.name} />;

    const lagData = lag.data;
    const publications = impact.data?.publications || [];

    return (
        <>
            <div className="mk-toolbar">
                <label className="mk-field">
                    <span>Métrica para el impacto</span>
                    <select value={metric} onChange={(event) => onMetricChange(event.target.value)}>
                        {Object.entries(GAME_METRICS).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                        ))}
                    </select>
                </label>
            </div>

            <div className="mk-stats">
                <StatTile
                    label="Mejor desfase"
                    value={lagData?.bestLag === null || lagData?.bestLag === undefined ? "—" : `${lagData.bestLag} día(s)`}
                    hint="Días entre las vistas y la reacción del juego"
                />
                <StatTile
                    label="Correlación"
                    value={formatNumber(lagData?.lags?.find((row) => row.lag === lagData?.bestLag)?.correlation)}
                    hint="Pearson, de −1 a 1"
                />
                <StatTile
                    label="Usuarios atribuidos"
                    value={formatNumber(sum(publications.map((item) => item.impact?.attributed)))}
                    hint={`${metricLabel} sobre lo esperado`}
                />
            </div>

            <ChartPanel
                title="Vistas de marketing vs. jugadores"
                subtitle="Cada serie está indexada a su promedio del periodo (100 = día promedio) para compararlas en el mismo eje. Pasa el cursor para ver los valores reales."
            >
                <div className="mk-series-toggles" role="group" aria-label="Series visibles">
                    {IMPACT_SERIES.map((item) => {
                        const active = visibleSeries.includes(item.key);
                        return (
                            <button
                                key={item.key}
                                type="button"
                                className={`mk-series-toggle${active ? " is-active" : ""}`}
                                aria-pressed={active}
                                onClick={() => toggleSeries(item.key)}
                            >
                                <span className={`mk-series-key mk-series-key--${item.type}`} style={{ background: item.color }} />
                                {item.label}
                            </button>
                        );
                    })}
                </div>
                <RequestState request={growth} empty={!combinedChart ? "Activa al menos una serie." : null}>
                    <C3Chart config={combinedChart} height={320} label="Vistas de marketing y jugadores por día" />
                </RequestState>
            </ChartPanel>

            <ChartPanel
                title="Impacto por publicación"
                subtitle="Compara la métrica en los días posteriores a cada publicación contra su línea base. Una ventana incompleta aún está en curso."
            >
                <RequestState request={impact} empty={publications.length === 0 ? "No hay publicaciones en este rango." : null}>
                    <div className="mk-table-wrap">
                        <table className="mk-table">
                            <thead>
                                <tr>
                                    <th>Publicación</th>
                                    <th className="mk-num">Vistas 7 d</th>
                                    <th className="mk-num">Esperado</th>
                                    <th className="mk-num">Observado</th>
                                    <th className="mk-num">Incremento</th>
                                    <th className="mk-num">Atribuidos</th>
                                    <th className="mk-num">Por 1.000 vistas</th>
                                </tr>
                            </thead>
                            <tbody>
                                {publications.map((item) => (
                                    <tr key={`${item.platform}-${item.url}`}>
                                        <td>
                                            <a href={safeUrl(item.url)} target="_blank" rel="noreferrer" className="mk-link">
                                                <PlatformName platform={item.platform} /> {item.title}
                                            </a>
                                            <span className="mk-muted"> · {new Date(item.publishDate).toLocaleDateString("es-CO")}</span>
                                            {!item.impact?.complete && <span className="mk-tag">En curso</span>}
                                            {item.impact?.overlappingWith?.length > 0 && (
                                                <span className="mk-tag" title={item.impact.overlappingWith.join(", ")}>
                                                    Se cruza con {item.impact.overlappingWith.length}
                                                </span>
                                            )}
                                        </td>
                                        <td className="mk-num">{formatNumber(item.views7d)}</td>
                                        <td className="mk-num">{formatNumber(item.impact?.expected)}</td>
                                        <td className="mk-num">{formatNumber(item.impact?.observed)}</td>
                                        <td className="mk-num">{formatPercent(item.impact?.upliftPct)}</td>
                                        <td className="mk-num">{formatNumber(item.impact?.attributed)}</td>
                                        <td className="mk-num">{formatNumber(item.impact?.perThousandViews)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </RequestState>
            </ChartPanel>
        </>
    );
}

// ── Dashboard ────────────────────────────────────────────────────────────────

export default function Dashboard() {
    const dispatch = useDispatch();
    const { projects, projectsStatus, projectsError, growth, summary, impact, lag, live } = useSelector((state) => state.marketing);
    const [projectId, setProjectId] = useState(readStoredProject);
    const [range, setRange] = useState(defaultRange);
    const [tab, setTab] = useState("users");
    const [metric, setMetric] = useState("newFromSearch");

    useEffect(() => {
        dispatch(fetchProjects());
    }, [dispatch]);

    const project = projects.find((item) => item._id === projectId) || projects[0];
    const activeId = project?._id;

    useEffect(() => {
        if (!activeId || range.from > range.to) return;
        storeProject(activeId);
        dispatch(fetchGrowth({ projectId: activeId, from: range.from, to: range.to }));
        dispatch(fetchSummary({ projectId: activeId, from: range.from, to: `${range.to}T23:59:59.999Z` }));
    }, [dispatch, activeId, range]);

    useEffect(() => {
        if (activeId && tab === "users") dispatch(fetchLiveGames({ projectId: activeId }));
    }, [dispatch, activeId, tab]);

    const hasGames = (growth.data?.games || []).length > 0;
    useEffect(() => {
        if (!activeId || tab !== "impact" || !hasGames || range.from > range.to) return;
        dispatch(fetchPostImpact({ projectId: activeId, from: range.from, to: range.to, metric }));
        dispatch(fetchLag({ projectId: activeId, from: range.from, to: range.to, metric }));
    }, [dispatch, activeId, tab, range, metric, hasGames]);

    const setPreset = (daysBack) => {
        const to = new Date(`${defaultRange().to}T00:00:00Z`);
        const from = new Date(to);
        from.setUTCDate(from.getUTCDate() - (daysBack - 1));
        setRange({ from: toDayKey(from), to: toDayKey(to) });
    };

    const updateRange = (key, value) => {
        if (!value) return;
        setRange((current) => ({ ...current, [key]: value }));
    };

    if (projectsStatus === "failed") return <div className="mk-center mk-error">{projectsError}</div>;
    if (projects.length === 0) {
        return projectsStatus === "succeeded" ? (
            <div className="mk-center">
                <span>Aún no hay proyectos de marketing.</span>
                <Link to="/Marketing/Accounts" className="mk-link">Crear un proyecto</Link>
            </div>
        ) : (
            <div className="mk-center"><div className="mk-spinner" /><span>Cargando proyectos…</span></div>
        );
    }

    const rangeError = range.from > range.to ? "La fecha inicial debe ser anterior a la final." : null;

    return (
        <div className="mk-scroll">
            <div className="mk-toolbar">
                <label className="mk-field">
                    <span>Proyecto</span>
                    <select className="select-large" value={activeId} onChange={(event) => setProjectId(event.target.value)}>
                        {projects.map((item) => (
                            <option key={item._id} value={item._id}>{item.name}</option>
                        ))}
                    </select>
                </label>
                <label className="mk-field">
                    <span>Desde</span>
                    <input type="date" value={range.from} max={range.to} onChange={(event) => updateRange("from", event.target.value)} />
                </label>
                <label className="mk-field">
                    <span>Hasta</span>
                    <input type="date" value={range.to} min={range.from} onChange={(event) => updateRange("to", event.target.value)} />
                </label>
                <div className="mk-presets">
                    {[7, 30, 90].map((daysBack) => (
                        <Button key={daysBack} variant="onlyText" size="small" onClick={() => setPreset(daysBack)}>
                            {daysBack} días
                        </Button>
                    ))}
                </div>
            </div>
            {rangeError && <p className="mk-error">{rangeError}</p>}

            <div className="mk-tabs" role="tablist" aria-label="Dashboards de marketing">
                {TABS.map((item) => (
                    <Button
                        key={item.key}
                        variant="outline"
                        size="small"
                        role="tab"
                        selected={tab === item.key}
                        aria-selected={tab === item.key}
                        onClick={() => setTab(item.key)}
                    >
                        {item.label}
                    </Button>
                ))}
            </div>

            {tab === "users" && <UsersTab growth={growth} live={live} project={project} />}
            {tab === "retention" && <RetentionTab growth={growth} summary={summary} project={project} />}
            {tab === "social" && <SocialTab growth={growth} summary={summary} project={project} range={range} />}
            {tab === "impact" && (
                <ImpactTab growth={growth} impact={impact} lag={lag} metric={metric} onMetricChange={setMetric} project={project} />
            )}
        </div>
    );
}
